// Разбор звонка: `GET /api/calls/{id}/telemetry` отдаёт куски трактa
// call.telemetry сырыми — их форма контракт наблюдаемости callv1, и своё DTO
// поверх него бэк не строит. Куски плоские: ход бота, реплика абонента,
// сегмент синтеза, консультация EOU, перебивание, провал пейсера — каждый со
// своим `turn_idx`. Сшивка по нему здесь: карточке нужен ход целиком.

import { bool, list, num, rec, span, stamp, str } from './parse'
import { type CallReport, type MediaStats, parseMedia } from './report'

// Абсолютные таймстемпы стадий (ISO; '' — стадия не наступила) плюс раскладка
// «кто съел время» в мс. Все таймстемпы измерены движком: у спекулятивного
// хода генерация стартует раньше EOU, у приветствия — в гудки.
export interface TurnTimeline {
  speechOnsetAt: string
  speechEndAt: string
  eouAt: string
  genStartAt: string
  llmFirstTokenAt: string
  llmLastTokenAt: string
  ttsFirstFrameAt: string
  faoAt: string
  audioEndAt: string
  turnEndAt: string
  eouWaitMs: number
  settleWaitMs: number
  llmTtftMs: number
  ttsTtfaMs: number
  faoMs: number
  perceivedMs: number
}

export interface DetectEvent {
  turnIdx: number
  seq: number
  prob: number
  threshold: number
  complete: boolean
  inferMs: number
  cascadeStage: string
  silenceMs: number
  at: string
}

export interface TurnEou {
  settleReason: string
  committedFinal: boolean
  grewDuringSettle: boolean
  speechSeq: number
  speechMs: number
  sttPartials: number
  sttFinals: number
  sttLagMs: number
  lateDropped: number
  detects: DetectEvent[]
}

export interface TurnLlm {
  model: string
  promptTokens: number
  cachedTokens: number
  completionTokens: number
  finishReason: string
  genMs: number
  genChars: number
  genText: string
  decideMs: number
  decideAction: string
  decideErr: boolean
  decidePromptTokens: number
  decideCachedTokens: number
}

export interface SegmentStat {
  turnIdx: number
  seq: number
  textLen: number
  synthStartAt: string
  queueWaitMs: number
  ttfaMs: number
  drainMs: number
  audioFrames: number
  starvedMs: number
  interrupted: boolean
  ttsErr: boolean
}

export interface PacerGap {
  turnIdx: number
  at: string
  gapFrames: number
}

export interface TurnDelivery {
  sentences: number
  enqueuedFrames: number
  emittedFrames: number
  deliveredChars: number
  firstSegChars: number
  maxStarvedMs: number
  ttsErrors: number
  pacerUnderruns: number
  pacerMissed: number
  pacerLate: number
  txWriteErrs: number
  txQueuedAtFao: number
  segments: SegmentStat[]
  pacerGaps: PacerGap[]
}

export interface TurnSpec {
  kind: string
  attempts: number
  gen: number
  leadMs: number
}

export interface BargeEvent {
  turnIdx: number
  at: string
  msSinceFao: number
  framesPlayed: number
  kind: string
  outcome: string
  premature: boolean
  speechSeq: number
}

export interface CallTurn {
  idx: number
  kind: string
  userText: string
  botText: string
  interrupted: boolean
  interruptReason: string
  resumeStreak: number
  nudgeStreak: number
  timeline: TurnTimeline
  eou: TurnEou
  llm: TurnLlm
  delivery: TurnDelivery
  spec: TurnSpec
  barges: BargeEvent[]
}

// Реплика абонента по VAD и её судьба: породила ход, склеилась, осталась
// пустой, удержана как бэкченнел, снята, проглочена окном приветствия.
export interface Utterance {
  seq: number
  onsetAt: string
  offsetAt: string
  durMs: number
  outcome: string
  turnIdx: number
  text: string
  reaction: string
}

// EOU-коммит без распознанного текста: почему бот решил, что абонент
// договорил, и что он на это сделал.
export interface EmptyCommit {
  at: string
  eouMs: number
  settleReason: string
  postBarge: boolean
  reaction: string
  streak: number
}

export interface NudgeEvent {
  at: string
  kind: string
  turnIdx: number
  streak: number
}

export interface CtrlEvent {
  at: string
  kind: string
}

export interface CallAnalysis {
  callId: string
  // Ось разбора: якорь уровня звонка. Пуст — кусок `call` не доехал, и время
  // событий берётся от отчёта.
  startedAt: string
  answeredAt: string
  endedAt: string
  media: MediaStats | null
  turns: CallTurn[]
  utterances: Utterance[]
  emptyCommits: EmptyCommit[]
  nudges: NudgeEvent[]
  ctrlEvents: CtrlEvent[]
  // Разбор вправе не доехать целиком: пустой — не ошибка, полноту называет
  // persist_ok шапки.
  empty: boolean
}

export interface ChatMessage {
  key: string
  role: 'bot' | 'user'
  text: string
  // мс от начала звонка; -1 — ось неизвестна
  atMs: number
  // Ход, из которого реплика: у фолбэка на transcript отчёта механики нет.
  turn: CallTurn | null
}

function parseTimeline(v: unknown): TurnTimeline {
  const t = rec(v)
  const speechEndAt = stamp(t.speech_end_at)
  const eouAt = stamp(t.eou_at)
  const genStartAt = stamp(t.gen_start_at)
  const llmFirstTokenAt = stamp(t.llm_first_token_at)
  const ttsFirstFrameAt = stamp(t.tts_first_frame_at)
  const faoAt = stamp(t.fao_at)
  const eouWaitMs = span(num(t.eou_wait_ms), speechEndAt, eouAt)
  const faoMs = span(num(t.fao_ms), eouAt, faoAt)
  return {
    speechOnsetAt: stamp(t.speech_onset_at),
    speechEndAt,
    eouAt,
    genStartAt,
    llmFirstTokenAt,
    llmLastTokenAt: stamp(t.llm_last_token_at),
    ttsFirstFrameAt,
    faoAt,
    audioEndAt: stamp(t.audio_end_at),
    turnEndAt: stamp(t.turn_end_at),
    eouWaitMs,
    settleWaitMs: num(t.settle_wait_ms),
    llmTtftMs: span(num(t.llm_ttft_ms), genStartAt || eouAt, llmFirstTokenAt),
    ttsTtfaMs: span(num(t.tts_ttfa_ms), llmFirstTokenAt, ttsFirstFrameAt),
    faoMs,
    // Пауза, которую слышит абонент: без конца речи её нет (приветствие, resume).
    perceivedMs: num(t.perceived_ms) || (speechEndAt ? span(0, speechEndAt, faoAt) : 0),
  }
}

function parseDetect(v: unknown): DetectEvent {
  const d = rec(v)
  return {
    turnIdx: num(d.turn_idx),
    seq: num(d.seq),
    prob: num(d.prob),
    threshold: num(d.threshold),
    complete: bool(d.complete),
    inferMs: num(d.infer_ms),
    cascadeStage: str(d.cascade_stage),
    silenceMs: num(d.silence_ms),
    at: stamp(d.at),
  }
}

function parseSegment(v: unknown): SegmentStat {
  const s = rec(v)
  return {
    turnIdx: num(s.turn_idx),
    seq: num(s.seq),
    textLen: num(s.text_len),
    synthStartAt: stamp(s.synth_start_at),
    queueWaitMs: num(s.queue_wait_ms),
    ttfaMs: num(s.ttfa_ms),
    drainMs: num(s.drain_ms),
    audioFrames: num(s.audio_frames),
    starvedMs: num(s.starved_ms),
    interrupted: bool(s.interrupted),
    ttsErr: bool(s.tts_err),
  }
}

function parseBarge(v: unknown): BargeEvent {
  const b = rec(v)
  return {
    turnIdx: num(b.turn_idx),
    at: stamp(b.at),
    msSinceFao: num(b.ms_since_fao),
    framesPlayed: num(b.frames_played),
    kind: str(b.kind),
    outcome: str(b.outcome),
    premature: bool(b.premature),
    speechSeq: num(b.speech_seq),
  }
}

function parsePacerGap(v: unknown): PacerGap {
  const g = rec(v)
  return { turnIdx: num(g.turn_idx), at: stamp(g.at), gapFrames: num(g.gap_frames) }
}

function parseTurn(v: unknown, fallbackIdx: number): CallTurn {
  const t = rec(v)
  const eou = rec(t.eou)
  const llm = rec(t.llm)
  const delivery = rec(t.delivery)
  const spec = rec(t.spec)
  return {
    idx: typeof t.turn_idx === 'number' ? t.turn_idx : fallbackIdx,
    kind: str(t.kind) || 'normal',
    userText: str(t.user_text),
    botText: str(t.bot_text),
    interrupted: bool(t.interrupted),
    interruptReason: str(t.interrupt_reason),
    resumeStreak: num(t.resume_streak),
    nudgeStreak: num(t.nudge_streak),
    timeline: parseTimeline(t.timeline),
    eou: {
      settleReason: str(eou.settle_reason),
      committedFinal: bool(eou.committed_final),
      grewDuringSettle: bool(eou.grew_during_settle),
      speechSeq: num(eou.speech_seq),
      speechMs: num(eou.speech_ms),
      sttPartials: num(eou.stt_partials),
      sttFinals: num(eou.stt_finals),
      sttLagMs: num(eou.stt_lag_ms),
      lateDropped: num(eou.late_dropped),
      detects: [],
    },
    llm: {
      model: str(llm.model),
      promptTokens: num(llm.prompt_tokens),
      cachedTokens: num(llm.cached_tokens),
      completionTokens: num(llm.completion_tokens),
      finishReason: str(llm.finish_reason),
      genMs: num(llm.gen_ms),
      genChars: num(llm.gen_chars),
      genText: str(llm.gen_text),
      decideMs: num(llm.decide_ms),
      decideAction: str(llm.decide_action),
      decideErr: bool(llm.decide_err),
      decidePromptTokens: num(llm.decide_prompt_tokens),
      decideCachedTokens: num(llm.decide_cached_tokens),
    },
    delivery: {
      sentences: num(delivery.sentences),
      enqueuedFrames: num(delivery.enqueued_frames),
      emittedFrames: num(delivery.emitted_frames),
      deliveredChars: num(delivery.delivered_chars),
      firstSegChars: num(delivery.first_seg_chars),
      maxStarvedMs: num(delivery.max_starved_ms),
      ttsErrors: num(delivery.tts_errors),
      pacerUnderruns: num(delivery.pacer_underruns),
      pacerMissed: num(delivery.pacer_missed),
      pacerLate: num(delivery.pacer_late),
      txWriteErrs: num(delivery.tx_write_errs),
      txQueuedAtFao: num(delivery.tx_queued_at_fao),
      segments: [],
      pacerGaps: [],
    },
    spec: {
      kind: str(spec.kind) || 'none',
      attempts: num(spec.attempts),
      gen: num(spec.gen),
      leadMs: num(spec.lead_ms),
    },
    barges: [],
  }
}

function parseUtterance(v: unknown): Utterance {
  const u = rec(v)
  return {
    seq: num(u.seq),
    onsetAt: stamp(u.onset_at),
    offsetAt: stamp(u.offset_at),
    durMs: num(u.dur_ms),
    outcome: str(u.outcome) || 'unfinished',
    turnIdx: num(u.turn_idx),
    text: str(u.text),
    reaction: str(u.reaction),
  }
}

function parseEmptyCommit(v: unknown): EmptyCommit {
  const c = rec(v)
  return {
    at: stamp(c.at),
    eouMs: num(c.eou_ms),
    settleReason: str(c.settle_reason),
    postBarge: bool(c.post_barge),
    reaction: str(c.reaction),
    streak: num(c.streak),
  }
}

// Кусок без своего хода в разборе показать негде — ход и есть единица чтения.
// Разбор доезжает кусками, и такой сирота означает потерянный TurnRecord.
function attach<T extends { turnIdx: number }>(
  items: T[],
  byIdx: Map<number, CallTurn>,
  put: (turn: CallTurn, item: T) => void,
): void {
  for (const item of items) {
    const turn = byIdx.get(item.turnIdx)
    if (turn) put(turn, item)
  }
}

export function parseCallTelemetry(body: unknown): CallAnalysis {
  const root = rec(body)
  const call = rec(root.call)
  const turns = list(root.turns)
    .map(parseTurn)
    .sort((a, b) => a.idx - b.idx)
  const byIdx = new Map(turns.map((t) => [t.idx, t]))

  attach(list(root.detects).map(parseDetect), byIdx, (turn, d) => turn.eou.detects.push(d))
  attach(list(root.segments).map(parseSegment), byIdx, (turn, s) => turn.delivery.segments.push(s))
  attach(list(root.barges).map(parseBarge), byIdx, (turn, b) => turn.barges.push(b))
  attach(list(root.pacer_gaps).map(parsePacerGap), byIdx, (turn, g) =>
    turn.delivery.pacerGaps.push(g),
  )
  for (const turn of turns) {
    turn.eou.detects.sort((a, b) => a.seq - b.seq)
    turn.delivery.segments.sort((a, b) => a.seq - b.seq)
    turn.barges.sort((a, b) => a.at.localeCompare(b.at))
  }

  const utterances = list(root.utterances)
    .map(parseUtterance)
    .sort((a, b) => a.seq - b.seq)
  const emptyCommits = list(root.empty_commits).map(parseEmptyCommit)
  const nudges = list(root.nudges).map((n) => {
    const e = rec(n)
    return {
      at: stamp(e.at),
      kind: str(e.kind),
      turnIdx: num(e.turn_idx),
      streak: num(e.streak),
    }
  })
  const ctrlEvents = list(root.ctrl_events).map((e) => {
    const c = rec(e)
    return { at: stamp(c.at), kind: str(c.kind) }
  })

  return {
    callId: str(root.call_id),
    startedAt: stamp(call.started_at),
    answeredAt: stamp(call.answered_at),
    endedAt: stamp(call.ended_at),
    media: parseMedia(call.media),
    turns,
    utterances,
    emptyCommits,
    nudges,
    ctrlEvents,
    empty:
      turns.length === 0 &&
      utterances.length === 0 &&
      emptyCommits.length === 0 &&
      nudges.length === 0 &&
      ctrlEvents.length === 0,
  }
}

export const emptyAnalysis = (callId = ''): CallAnalysis => ({
  callId,
  startedAt: '',
  answeredAt: '',
  endedAt: '',
  media: null,
  turns: [],
  utterances: [],
  emptyCommits: [],
  nudges: [],
  ctrlEvents: [],
  empty: true,
})

/**
 * Диалог собирается из ходов разбора: там и тексты, и механика под каждой
 * репликой. Разбор, который не доехал, оставляет только транскрипт отчёта —
 * тогда пузыри приходят из него, с той осью, что он принёс.
 */
export function callChat(analysis: CallAnalysis, report: CallReport): ChatMessage[] {
  const startedAt = report.startedAt || analysis.startedAt
  const at = (iso: string) => {
    if (!startedAt || !iso) return -1
    const delta = Date.parse(iso) - Date.parse(startedAt)
    return Number.isFinite(delta) && delta >= 0 ? Math.round(delta) : -1
  }

  const out: ChatMessage[] = []
  for (const turn of analysis.turns) {
    if (turn.userText) {
      out.push({
        key: `turn-${turn.idx}-user`,
        role: 'user',
        text: turn.userText,
        atMs: at(turn.timeline.speechOnsetAt),
        turn,
      })
    }
    if (turn.botText) {
      out.push({
        key: `turn-${turn.idx}-bot`,
        role: 'bot',
        text: turn.botText,
        atMs: at(turn.timeline.faoAt),
        turn,
      })
    }
  }
  if (out.length > 0) return out

  return report.transcript.map((segment, i) => ({
    key: `segment-${i}`,
    role: segment.role,
    text: segment.text,
    atMs: segment.tStartMs > 0 ? segment.tStartMs : -1,
    turn: null,
  }))
}
